import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ClientSidenav from '../components/ClientSidenav';
import './MeusCursos.css';

function MeusCursos() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const user = JSON.parse(localStorage.getItem('user_data') || '{}');

  useEffect(() => {
    if (!user.email) { navigate('/treinamentos'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [enrollmentsRes, coursesRes] = await Promise.all([
        api.get('/enrollments', true),
        api.get('/courses')
      ]);
      if (enrollmentsRes.success && coursesRes.success) {
        const enrolledCourses = enrollmentsRes.data.map(enrollment => {
          const course = coursesRes.data.find(c => c.id === enrollment.courseId);
          return { ...enrollment, ...course };
        });
        setCourses(enrolledCourses);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async (courseId, courseName) => {
    if (!window.confirm(`Tem certeza que deseja se desinscrever de "${courseName}"?`)) return;
    try {
      const response = await api.delete(`/enrollments/${courseId}`, true);
      if (response.success) {
        alert('Desinscrição realizada com sucesso!');
        loadData();
      } else {
        alert('Erro: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error) {
      alert('Erro ao desinscrever: ' + error.message);
    }
  };

  const inProgress = courses.filter(c => (c.progress || 0) > 0 && (c.progress || 0) < 100);
  const completed = courses.filter(c => c.progress === 100);
  const notStarted = courses.filter(c => !c.progress || c.progress === 0);

  if (loading) {
    return (
      <div className="dark-loading">
        <div className="dark-spinner"/>
        <p>Carregando seus cursos...</p>
      </div>
    );
  }

  const CourseCard = ({ course, variant }) => {
    const progress = course.progress || 0;
    const isListMode = viewMode === 'list';

    return (
      <div className={`mc-course-card${isListMode ? ' list-mode' : ''}`}>
        <button
          className="mc-btn-unenroll"
          onClick={() => handleUnenroll(course.id, course.title)}
          title="Desinscrever"
        >
          ×
        </button>
        {course.coverImage && (
          <img src={course.coverImage} alt={course.title} className="mc-cover"/>
        )}
        <div className="mc-card-body">
          <div className="mc-card-title">{course.title}</div>
          {isListMode && course.description && (
            <div className="mc-card-desc">{course.description}</div>
          )}

          {variant !== 'completed' && (
            <div className="mc-progress-bar">
              <div
                className="mc-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          <div className="mc-card-footer">
            <span className="mc-enrolled-date">
              {variant === 'completed'
                ? `Concluído em ${new Date(course.enrolledAt).toLocaleDateString('pt-BR')}`
                : variant === 'notStarted'
                ? `Inscrito em ${new Date(course.enrolledAt).toLocaleDateString('pt-BR')}`
                : `Iniciado em ${new Date(course.enrolledAt).toLocaleDateString('pt-BR')}`
              }
            </span>

            {variant === 'inProgress' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mc-progress-badge">{progress}%</span>
                <button className="mc-btn-action mc-btn-continue" onClick={() => navigate(`/treinamentos/curso/${course.id}`)}>
                  Retomar
                </button>
              </div>
            )}
            {variant === 'completed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mc-completed-badge">✓ Concluído</span>
                <button className="mc-btn-action mc-btn-review" onClick={() => navigate(`/treinamentos/curso/${course.id}`)}>
                  Revisar
                </button>
              </div>
            )}
            {variant === 'notStarted' && (
              <button className="mc-btn-action mc-btn-start" onClick={() => navigate(`/treinamentos/curso/${course.id}`)}>
                Iniciar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="meus-cursos">
      <ClientSidenav />

      <div className="mc-content">
        <h1 className="mc-page-title">
          Meus <span>Cursos</span>
        </h1>

        {/* Stats */}
        <div className="mc-stats">
          <div className="mc-stat-card stat-total">
            <span className="mc-stat-number">{courses.length}</span>
            <span className="mc-stat-label">Total de cursos</span>
          </div>
          <div className="mc-stat-card stat-completed">
            <span className="mc-stat-number">{completed.length}</span>
            <span className="mc-stat-label">Concluídos</span>
          </div>
          <div className="mc-stat-card stat-progress">
            <span className="mc-stat-number">{inProgress.length}</span>
            <span className="mc-stat-label">Em andamento</span>
          </div>
          <div className="mc-stat-card stat-pending">
            <span className="mc-stat-number">{notStarted.length}</span>
            <span className="mc-stat-label">Não iniciados</span>
          </div>
        </div>

        {/* Pie chart */}
        {courses.length > 0 && (
          <div className="mc-chart-card">
            <div
              className="pie-chart"
              style={{
                background: `conic-gradient(
                  #4ade80 0deg ${completed.length / courses.length * 360}deg,
                  #818cf8 ${completed.length / courses.length * 360}deg ${(completed.length + inProgress.length) / courses.length * 360}deg,
                  #fb923c ${(completed.length + inProgress.length) / courses.length * 360}deg 360deg
                )`
              }}
            >
              <div className="pie-center">
                <span className="pie-percentage">
                  {Math.round(completed.length / courses.length * 100)}%
                </span>
                <span className="pie-label">Concluído</span>
              </div>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-dot completed-color"/>
                Concluídos ({completed.length})
              </div>
              <div className="legend-item">
                <span className="legend-dot progress-color"/>
                Em Andamento ({inProgress.length})
              </div>
              <div className="legend-item">
                <span className="legend-dot notstarted-color"/>
                Não Iniciados ({notStarted.length})
              </div>
            </div>
          </div>
        )}

        {/* View toggle */}
        {courses.length > 0 && (
          <div className="mc-view-toggle">
            <button
              className={`mc-view-btn${viewMode === 'grid' ? ' active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <rect x="0" y="0" width="7" height="7" rx="1.5"/>
                <rect x="9" y="0" width="7" height="7" rx="1.5"/>
                <rect x="0" y="9" width="7" height="7" rx="1.5"/>
                <rect x="9" y="9" width="7" height="7" rx="1.5"/>
              </svg>
              Grade
            </button>
            <button
              className={`mc-view-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <rect x="0" y="2" width="16" height="2" rx="1"/>
                <rect x="0" y="7" width="16" height="2" rx="1"/>
                <rect x="0" y="12" width="16" height="2" rx="1"/>
              </svg>
              Lista
            </button>
          </div>
        )}

        {/* Sections */}
        {inProgress.length > 0 && (
          <section className="mc-section">
            <div className="mc-section-title">
              <span className="mc-section-dot dot-progress"/>
              Em Andamento
            </div>
            <div className={viewMode === 'grid' ? 'mc-grid' : 'mc-list'}>
              {inProgress.map(c => <CourseCard key={c.id} course={c} variant="inProgress"/>)}
            </div>
          </section>
        )}

        {completed.length > 0 && (
          <section className="mc-section">
            <div className="mc-section-title">
              <span className="mc-section-dot dot-completed"/>
              Concluídos
            </div>
            <div className={viewMode === 'grid' ? 'mc-grid' : 'mc-list'}>
              {completed.map(c => <CourseCard key={c.id} course={c} variant="completed"/>)}
            </div>
          </section>
        )}

        {notStarted.length > 0 && (
          <section className="mc-section">
            <div className="mc-section-title">
              <span className="mc-section-dot dot-pending"/>
              Não Iniciados
            </div>
            <div className={viewMode === 'grid' ? 'mc-grid' : 'mc-list'}>
              {notStarted.map(c => <CourseCard key={c.id} course={c} variant="notStarted"/>)}
            </div>
          </section>
        )}

        {courses.length === 0 && (
          <div className="mc-empty">
            <div className="mc-empty-icon">📚</div>
            <h3>Você ainda não tem cursos</h3>
            <p>Explore nosso catálogo e comece sua jornada de aprendizado!</p>
            <button className="mc-btn-catalog" onClick={() => navigate('/treinamentos/cursos')}>
              Ver Catálogo de Cursos
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MeusCursos;
